const ELFSIGHT_SCRIPT_HOST_RE = /^(?:elfsightcdn\.com|static\.elfsight\.com|apps\.elfsight\.com)$/i;
const ELFSIGHT_MOUNT_CLASS_RE = /^elfsight-app-[a-z0-9-]+$/i;
const DEFAULT_PLATFORM_SCRIPT =
  '<script src="https://elfsightcdn.com/platform.js" async defer></script>';

export function isTrustedElfsightScriptSrc(src: string): boolean {
  try {
    const url = new URL(src.startsWith('//') ? `https:${src}` : src);
    return (
      (url.protocol === 'https:' || url.protocol === 'http:') &&
      ELFSIGHT_SCRIPT_HOST_RE.test(url.hostname)
    );
  } catch {
    return false;
  }
}

function isElfsightMount(el: Element): boolean {
  return Array.from(el.classList).some((className) => ELFSIGHT_MOUNT_CLASS_RE.test(className));
}

// Elfsight embeds are the one kind we let run with access to the page they sit
// in, because their widgets do not work otherwise. Anything else is refused, so
// this checks the embed really is Elfsight and returns nothing when it is not.
//
// The scripts have to be written out again by hand, since reading the HTML back
// out of an element silently drops them. An embed that has the widget markup but
// not the script that loads it is given the standard Elfsight loader.
export function normalizeTrustedElfsightHtml(html: string): string | null {
  const trimmed = html.trim();
  if (!trimmed || !/elfsight-app-/i.test(trimmed)) return null;

  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(trimmed, 'text/html');
  } catch {
    return null;
  }

  const mounts = Array.from(doc.body.querySelectorAll('*')).filter(isElfsightMount);
  if (mounts.length === 0) return null;

  const scripts = Array.from(doc.querySelectorAll('script'));
  const scriptTags: string[] = [];
  const seenSrc = new Set<string>();

  if (scripts.length === 0) {
    scriptTags.push(DEFAULT_PLATFORM_SCRIPT);
  } else {
    for (const script of scripts) {
      const src = script.getAttribute('src')?.trim() ?? '';
      if (!src || !isTrustedElfsightScriptSrc(src) || (script.textContent ?? '').trim()) {
        return null;
      }
      if (seenSrc.has(src)) continue;
      seenSrc.add(src);
      scriptTags.push(script.outerHTML);
    }
  }

  const mountTags = mounts.map((mount) => {
    mount.replaceChildren();
    mount.setAttribute('data-elfsight-app-lazy', '');
    return mount.outerHTML;
  });

  return [...scriptTags, ...mountTags].join('\n');
}
